import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { Stack } from "types/generated";
import { AccessResult, hasAdminAccess, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";

type StackForAdminAccess = Pick<Stack, "spaceDetails" | "space" | "isLockedByTemplateDeployment">;

/**
 * Hook that returns a function to check if the current user has admin access to a stack.
 * Stacks created from a template deployment cannot be managed.
 */
const useHasStackAdminAccess = (): ((stack?: StackForAdminAccess) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack) => {
      if (!stack) {
        return { result: false };
      }

      const templateDeploymentCheck = checkTemplateDeploymentAccess(stack);
      if (templateDeploymentCheck) {
        return templateDeploymentCheck;
      }

      if (viewer.admin) {
        return { result: true };
      }

      const hasAccess = hasAdminAccess(viewer, stack.spaceDetails?.id || stack.space);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer]
  );
};

export default useHasStackAdminAccess;
